/**
 * Browser-side rematch placement lock.
 *
 * API `IfcModel#transform` permit + `transform_hash` keep only
 * x/y/z/rotX/rotY/rotZ as floats. `federationCleared` / `placed` cannot be
 * stored without editing the dirty admin-ops API branch. Identity rematch
 * (0,0,0) therefore looks "unplaced" on the next GET — reload would apply
 * IfcMapConversion again. This store is the durable flag for this browser.
 */

const keyFor = (projectId) => `promang.ifc.placement.${projectId}`;

export function readPlacements(projectId) {
  if (!projectId || typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(keyFor(projectId));
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (_) {
    return {};
  }
}

export function readPlacement(projectId, modelId) {
  const all = readPlacements(projectId);
  return all[String(modelId)] || null;
}

export function writePlacement(projectId, modelId, payload) {
  if (!projectId || !modelId || typeof window === "undefined") return;
  const all = readPlacements(projectId);
  all[String(modelId)] = {
    transform: payload?.transform || null,
    federationCleared: payload?.federationCleared !== false,
    uprightConfirmed: payload?.uprightConfirmed === true,
    updatedAt: Date.now(),
  };
  try {
    window.localStorage.setItem(keyFor(projectId), JSON.stringify(all));
  } catch (_) {
    /* quota / private mode — session still has the live transform */
  }
}

export function writePlacements(projectId, rows) {
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    if (!row?.modelId || !row.transform) return;
    writePlacement(projectId, row.modelId, {
      transform: row.transform,
      federationCleared: true,
      uprightConfirmed: row.uprightConfirmed === true,
    });
  });
}

/** Replace creates a new IfcModel id — copy the locked placement onto it. */
export function copyPlacement(projectId, fromId, toId) {
  if (!projectId || !fromId || !toId) return false;
  if (String(fromId) === String(toId)) return false;
  const payload = readPlacement(projectId, fromId);
  if (!payload?.transform) return false;
  writePlacement(projectId, toId, payload);
  return true;
}
